import useIsNarrow from "../hooks/useIsNarrow";

/**
 * "How is this calculated?" for the IRR report.
 *
 * Written against what irrCalculator.js actually does, not IRR in general — the numbers in
 * the worked example below are the real output of generateCashFlowProjection for those
 * inputs, so if the model changes this text is wrong and should be changed with it.
 */
export default function IRRExplainerModal({ isOpen, onClose, rentGrowthRate = 5, propertyAppreciationRate = 6, managementFeePercent = 0 }) {
  const narrow = useIsNarrow();
  if (!isOpen) return null;

  const h3 = { fontSize: "15px", fontWeight: "700", color: "#333", margin: "22px 0 8px" };
  const p = { fontSize: "14px", lineHeight: 1.65, color: "#444", margin: "0 0 10px" };
  const th = { textAlign: "left", padding: "7px 10px", borderBottom: "2px solid #e0e0e0",
               fontSize: "13px", color: "#555" };
  const td = { padding: "7px 10px", borderBottom: "1px solid #eee", fontSize: "13px" };
  const num = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

  return (
    <div
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1100,
               display: "flex", alignItems: "center", justifyContent: "center",
               padding: narrow ? "12px" : "24px" }}
      onClick={onClose}
    >
      <div
        style={{ backgroundColor: "#fff", borderRadius: "10px", maxWidth: "720px", width: "100%",
                 maxHeight: "88vh", overflowY: "auto", padding: narrow ? "20px 16px" : "28px 32px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      borderBottom: "2px solid #000", paddingBottom: "10px", marginBottom: "4px" }}>
          <h2 style={{ margin: 0, fontSize: narrow ? "18px" : "21px" }}>How the IRR is calculated</h2>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: "none", border: "none", fontSize: "26px", lineHeight: 1,
                           cursor: "pointer", color: "#666" }}>×</button>
        </div>

        <h3 style={h3}>The short version</h3>
        <p style={p}>
          IRR is the annual return that makes this deal break even. You put money in once, take
          money out every year, and sell in year 10. The IRR is the single interest rate at
          which all of that cancels out — so <strong>higher is better</strong>, and it is
          directly comparable to any other investment quoted as a yearly percentage.
        </p>
        <p style={p}>
          It differs from cap rate in one important way: cap rate looks at a single year and
          ignores the mortgage. IRR covers the whole ten years and counts the loan, the rent
          growth and the eventual sale.
        </p>

        <h3 style={h3}>What goes in, what comes out</h3>
        <p style={p}>
          <strong>Money out, once:</strong> your Total Out of Pocket — the down payment, the
          closing costs, and any renovation budget. Not the purchase price: the part of the
          price covered by the mortgage is not your cash.
        </p>
        <p style={p}>
          <strong>Money in, each year:</strong> rent, minus taxes and common charges, minus the
          mortgage payment. This is the same cash flow figure shown in the Rental Value
          Analysis, and it can be negative.
        </p>
        {managementFeePercent > 0 && (
          <p style={p}>
            <strong>Management:</strong> from year 2, {managementFeePercent}% of the rent
            collected comes off that cash flow. Year 1 is the lease-up and is not charged. You
            can switch it off, or change the percentage, in IRR Assumptions.
          </p>
        )}
        <p style={p}>
          <strong>Money in, year 10:</strong> the sale. The property is assumed to have
          appreciated {propertyAppreciationRate}% a year; selling costs of 6.825% come off, then
          the outstanding mortgage is repaid. What is left is added to that final year.
        </p>

        <h3 style={h3}>A simple example</h3>
        <p style={p}>
          A $1,000,000 apartment, 25% down, interest-only at 5.75%. You bring $250,000 of
          deposit plus about $35,000 of closing costs &mdash; <strong>$285,000 out of
          pocket</strong>. It rents for $5,500 a month against $2,100 of taxes and common
          charges and $3,594 of interest, so it runs at a small loss.
          {managementFeePercent > 0 && " The figures below are before any management fee."}
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: "10px" }}>
            <thead>
              <tr>
                <th style={th}>Year</th>
                <th style={{ ...th, textAlign: "right" }}>Cash flow</th>
                <th style={{ ...th, textAlign: "right" }}>Property value</th>
                <th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={td}>0</td><td style={num}>&minus;$285,000</td><td style={num}>$1,000,000</td><td style={td}>what you put in</td></tr>
              <tr><td style={td}>1</td><td style={num}>&minus;$2,328</td><td style={num}>$1,060,000</td><td style={td}>rent does not quite cover costs</td></tr>
              <tr><td style={td}>5</td><td style={num}>&minus;$1,896</td><td style={num}>$1,338,226</td><td style={td}>the shortfall is shrinking</td></tr>
              <tr><td style={td}>10</td><td style={num}>+$917,155</td><td style={num}>$1,790,848</td><td style={td}>sale, less 6.825% costs and the $750,000 loan</td></tr>
            </tbody>
          </table>
        </div>
        <p style={p}>
          $285,000 in, ten years of small losses, and about $917,000 back at the end. The rate
          that balances those is <strong>12.0%</strong> &mdash; that is the IRR.
        </p>
        <p style={p}>
          Two things are worth noticing. The deal <strong>never turns cash-flow positive</strong>
          &mdash; it is still losing about $1,500 in year ten &mdash; and it is still a good
          return, because essentially all of it comes from the sale. And the ten years of
          losses add up to roughly $19,000 against a $917,000 exit, which is why the yearly
          figures barely move the result.
        </p>

        <h3 style={h3}>What moves the number</h3>
        <p style={p}>
          Each row below is that one change applied to the example above, which starts at
          <strong> 12.0%</strong>. The sizes are as telling as the directions.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={th}>Change</th>
                <th style={{ ...th, textAlign: "right" }}>IRR</th>
                <th style={th}>Why</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={td}>Appreciation {propertyAppreciationRate}% &rarr; 8%</td><td style={num}><strong>+3.7 pts</strong></td><td style={td}>Nearly all the return is the year-10 sale.</td></tr>
              <tr><td style={td}>Appreciation {propertyAppreciationRate}% &rarr; 4%</td><td style={num}><strong>&minus;4.2 pts</strong></td><td style={td}>Same lever the other way, and the largest single influence on the result.</td></tr>
              <tr><td style={td}>Down payment 25% &rarr; 15%</td><td style={num}>+2.0 pts</td><td style={td}>Less of your own cash against the same gain. Leverage.</td></tr>
              <tr><td style={td}>Mortgage rate 5.75% &rarr; 7.25%</td><td style={num}>&minus;2.0 pts</td><td style={td}>Interest comes off every year&rsquo;s cash flow.</td></tr>
              <tr><td style={td}>Rent +$500/month</td><td style={num}>+1.4 pts</td><td style={td}>Ten years of a better monthly figure.</td></tr>
              <tr><td style={td}>Purchase price &minus;10%</td><td style={num}>+0.9 pts</td><td style={td}>Cheaper entry, smaller deposit, same future value.</td></tr>
              <tr><td style={td}>Renovation +$25,000</td><td style={num}>&minus;0.9 pts</td><td style={td}>Raises the cash-in figure everything is measured against.</td></tr>
              <tr><td style={td}>Taxes or charges +$300/month</td><td style={num}>&minus;0.7 pts</td><td style={td}>Reduces cash flow for ten years running.</td></tr>
              <tr><td style={td}>Rent growth {rentGrowthRate}% &rarr; 8%</td><td style={num}>+0.04 pts</td><td style={td}><strong>Almost nothing.</strong> It compounds a few thousand a year against a $917,000 exit.</td></tr>
            </tbody>
          </table>
        </div>
        <p style={p}>
          The surprise is usually that last row. Rent growth feels like it should matter and
          barely registers, while the appreciation rate &mdash; an assumption, not a
          measurement &mdash; is worth four points on its own.
        </p>

        <h3 style={h3}>Worth knowing</h3>
        <p style={p}>
          The appreciation and rent growth rates are <strong>assumptions, not forecasts</strong>.
          They are the two inputs you can change at the top of the report, and because the sale
          dominates the result, the appreciation rate deserves the most scrutiny — a point of
          appreciation is worth far more here than a point of rent.
        </p>
        <p style={p}>
          The projection also assumes the property is held for the full ten years and sold at the
          end, that it is never vacant, and that rent is collected every month.
          {managementFeePercent > 0 && ` The ${managementFeePercent}% management fee is charged on
            that full year's rent from year 2 on.`}
        </p>
      </div>
    </div>
  );
}
